import ModuleBase from '../shared/ModuleBase.js';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Navigation — A11y-konformes Disclosure-Pattern (W3C APG Disclosure-Navigation).
 * Desktop: Hover + Tastatur-Toggle, Focus-Return, ESC schließt.
 * Mobile: Burger mit Focus-Trap, Submenu-Toggle pro Item, Focus-Return.
 */
export default class Navigation extends ModuleBase {
    constructor(el) {
        super(el);
        this._initBurger();
        this._initMobile();
        this._initDesktopFlyouts();
        this._initGlobalEscape();
        this._initOutsideClick();
    }

    /* ----------- Burger (Mobile) ----------- */

    _initBurger() {
        this._burgerToggle = this.$el.querySelector('[data-burger="toggle"]');
        this._burgerPanel = this.$el.querySelector('[data-burger="panel"]');
        if (!this._burgerToggle) return;

        this._labelOpen = this._burgerToggle.dataset.labelOpen || 'Menü öffnen';
        this._labelClose = this._burgerToggle.dataset.labelClose || 'Menü schließen';

        this._burgerToggle.addEventListener('click', (e) => {
            e.preventDefault();
            this._isBurgerOpen() ? this._closeBurger() : this._openBurger();
        });

        if (this._burgerPanel) {
            this._burgerPanel.addEventListener('keydown', (e) => this._trapFocus(e));
        }
    }

    _openBurger() {
        this.$el.setAttribute('aria-expanded', 'true');
        if (this._burgerToggle) {
            this._burgerToggle.setAttribute('aria-expanded', 'true');
            this._burgerToggle.setAttribute('aria-label', this._labelClose);
        }
        document.body.style.overflow = 'hidden';
    }

    _closeBurger({ returnFocus = false } = {}) {
        this.$el.setAttribute('aria-expanded', 'false');
        if (this._burgerToggle) {
            this._burgerToggle.setAttribute('aria-expanded', 'false');
            this._burgerToggle.setAttribute('aria-label', this._labelOpen);
        }
        document.body.style.overflow = '';
        if (this._mobileContainers) {
            this._mobileContainers.forEach((c) => this._closeMobile(c));
        }
        if (returnFocus && this._burgerToggle) this._burgerToggle.focus();
    }

    _isBurgerOpen() {
        return this.$el.getAttribute('aria-expanded') === 'true';
    }

    _trapFocus(e) {
        if (e.key !== 'Tab' || !this._isBurgerOpen()) return;
        if (window.matchMedia('(min-width: 1024px)').matches) return;

        const focusables = Array.from(this._burgerPanel.querySelectorAll(FOCUSABLE_SELECTOR))
            .filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
        // Burger selbst auch in Tab-Cycle einbeziehen
        focusables.unshift(this._burgerToggle);

        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }

    /* ----------- Mobile-Submenu pro Item ----------- */

    _initMobile() {
        this._mobileContainers = this.$el.querySelectorAll('[data-megamenu="container"]');

        this._mobileContainers.forEach((container) => {
            const trigger = container.querySelector('[data-megamenu="trigger"]');
            if (!trigger) return;
            // Nur auf Mobile-Breakpoint reagieren (Desktop nutzt Flyout-Hover/Keyboard)
            trigger.addEventListener('click', (e) => {
                if (window.matchMedia('(min-width: 1024px)').matches) return;
                e.preventDefault();
                this._isMobileOpen(container) ? this._closeMobile(container) : this._openMobile(container);
            });
        });
    }

    _openMobile(container) {
        const trigger = container.querySelector('[data-megamenu="trigger"]');
        const panel = container.querySelector('[data-megamenu="panel-mobile"]');
        const chevron = container.querySelector('[data-megamenu="chevron"]');
        if (panel) panel.style.display = '';
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        if (chevron) chevron.classList.add('rotate-180');
    }

    _closeMobile(container) {
        const trigger = container.querySelector('[data-megamenu="trigger"]');
        const panel = container.querySelector('[data-megamenu="panel-mobile"]');
        const chevron = container.querySelector('[data-megamenu="chevron"]');
        if (panel) panel.style.display = 'none';
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (chevron) chevron.classList.remove('rotate-180');
    }

    _isMobileOpen(container) {
        const trigger = container.querySelector('[data-megamenu="trigger"]');
        return trigger && trigger.getAttribute('aria-expanded') === 'true';
    }

    /* ----------- Desktop Flyout (Hover + Keyboard) ----------- */

    _initDesktopFlyouts() {
        this._flyoutContainers = this.$el.querySelectorAll('[data-flyout="container"]');

        this._flyoutContainers.forEach((container) => {
            const trigger = container.querySelector('[data-flyout="trigger"]');
            const panel = container.querySelector('[data-flyout="panel"]');
            let closeTimer = null;

            container.addEventListener('mouseenter', () => {
                if (!window.matchMedia('(min-width: 1024px)').matches) return;
                clearTimeout(closeTimer);
                this._openFlyout(container);
            });

            container.addEventListener('mouseleave', () => {
                if (!window.matchMedia('(min-width: 1024px)').matches) return;
                closeTimer = setTimeout(() => this._closeFlyout(container), 150);
            });

            if (trigger) {
                // Klick auf Disclosure-Button toggelt Flyout (auf Desktop)
                trigger.addEventListener('click', (e) => {
                    if (!window.matchMedia('(min-width: 1024px)').matches) return;
                    e.preventDefault();
                    this._isFlyoutOpen(container) ? this._closeFlyout(container) : this._openFlyout(container);
                });

                trigger.addEventListener('keydown', (e) => {
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        this._openFlyout(container);
                        this._focusFirstItem(panel);
                    }
                });
            }

            if (panel) {
                panel.addEventListener('keydown', (e) => {
                    const items = this._panelItems(panel);
                    const currentIndex = items.indexOf(document.activeElement);

                    if (e.key === 'ArrowDown' && items.length > 0) {
                        e.preventDefault();
                        const next = currentIndex + 1 < items.length ? currentIndex + 1 : 0;
                        items[next].focus();
                    }
                    if (e.key === 'ArrowUp' && items.length > 0) {
                        e.preventDefault();
                        const prev = currentIndex - 1 >= 0 ? currentIndex - 1 : items.length - 1;
                        items[prev].focus();
                    }
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        this._closeFlyout(container, { returnFocus: true });
                    }
                    if (e.key === 'Tab') {
                        const isLast = currentIndex === items.length - 1 && !e.shiftKey;
                        const isFirst = currentIndex === 0 && e.shiftKey;
                        if (isLast || isFirst) this._closeFlyout(container);
                    }
                });
            }
        });
    }

    _panelItems(panel) {
        return Array.from(panel.querySelectorAll('a[href]')).filter((el) => el.offsetParent !== null);
    }

    _focusFirstItem(panel) {
        if (!panel) return;
        const items = this._panelItems(panel);
        if (items.length > 0) items[0].focus();
    }

    _openFlyout(container) {
        const trigger = container.querySelector('[data-flyout="trigger"]');
        const panel = container.querySelector('[data-flyout="panel"]');
        const chevron = container.querySelector('[data-megamenu="chevron"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        if (panel) {
            panel.classList.remove('invisible', 'opacity-0');
            panel.classList.add('visible', 'opacity-100');
        }
        if (chevron) chevron.classList.add('rotate-180');
    }

    _closeFlyout(container, { returnFocus = false } = {}) {
        const trigger = container.querySelector('[data-flyout="trigger"]');
        const panel = container.querySelector('[data-flyout="panel"]');
        const chevron = container.querySelector('[data-megamenu="chevron"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (panel) {
            panel.classList.add('invisible', 'opacity-0');
            panel.classList.remove('visible', 'opacity-100');
        }
        if (chevron) chevron.classList.remove('rotate-180');
        if (returnFocus && trigger) trigger.focus();
    }

    _isFlyoutOpen(container) {
        const trigger = container.querySelector('[data-flyout="trigger"]');
        return trigger && trigger.getAttribute('aria-expanded') === 'true';
    }

    /* ----------- Global ESC + Outside-Click ----------- */

    _initGlobalEscape() {
        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;

            // Burger zuerst (Mobile)
            if (this._isBurgerOpen()) {
                this._closeBurger({ returnFocus: true });
                return;
            }

            // Offene Flyouts schließen + Focus zurück zum Trigger
            this._flyoutContainers.forEach((c) => {
                if (this._isFlyoutOpen(c)) this._closeFlyout(c, { returnFocus: true });
            });
        });
    }

    _initOutsideClick() {
        document.addEventListener('click', (e) => {
            this._flyoutContainers.forEach((container) => {
                if (!container.contains(e.target)) this._closeFlyout(container);
            });
        });
    }
}
